// Shared payout/redemption contract for desktop + mobile (plan §12).
// Capability-driven: methods, limits, fees and delivery copy all come from the server
// (/payouts/options) — NO hardcoded method lists or min/max amounts live in the client.
// Amounts use the server's { minor, currency, display } shape; never parse raw floats.
import api from "@/lib/api";

// ---- money helpers ---------------------------------------------------------
export function fmtMoney(amount) {
  // amount is { minor, currency, display } or null
  if (!amount) return "$0.00";
  if (amount.display != null) return `$${amount.display}`;
  const minor = Number(amount.minor);
  if (!Number.isFinite(minor)) return "$0.00"; // guard a null/undefined minor (no more "$NaN")
  return `$${(minor / 100).toFixed(2)}`;
}

export function minorFromInput(str) {
  // Parse a user-entered decimal string into integer minor units WITHOUT float drift.
  if (typeof str !== "string") str = String(str ?? "");
  const cleaned = str.replace(/[^0-9.]/g, "");
  if (!cleaned) return 0;
  const [whole, frac = ""] = cleaned.split(".");
  const cents = (frac + "00").slice(0, 2);
  return parseInt(whole || "0", 10) * 100 + parseInt(cents || "0", 10);
}

// ---- player API ------------------------------------------------------------
export const payoutsApi = {
  options: () => api.get("/payouts/options").then((r) => r.data),
  destinations: () => api.get("/payouts/destinations").then((r) => r.data),
  // Connect a manual P2P handle (Cash App / Venmo / PayPal / Zelle). Stored unverified until an
  // operator confirms ownership (manual_review). Available only when the manual rail is enabled.
  addManualDestination: (method, handle) =>
    api.post("/payouts/destinations/manual", { method, handle }).then((r) => r.data),
  quote: (body) => api.post("/payouts/quote", body).then((r) => r.data),
  create: (body) => api.post("/payouts/redemptions", body).then((r) => r.data),
  history: () => api.get("/payouts/redemptions").then((r) => r.data),
  detail: (ref) => api.get(`/payouts/redemptions/${ref}`).then((r) => r.data),
  cancel: (ref, reason) =>
    api.post(`/payouts/redemptions/${ref}/cancel`, { reason }).then((r) => r.data),
};

// ---- admin API -------------------------------------------------------------
// Obtain a server-minted, single-use, actor+action-bound step-up proof for a sensitive
// action, then immediately use it. In production the backend performs the real MFA
// challenge before issuing; here it is capability-gated. Returns the JWT token string.
async function withStepUp(action, fn) {
  const { token } = await api
    .post("/admin/payouts/step-up", { action })
    .then((r) => r.data);
  return fn(token);
}

export const adminPayoutsApi = {
  stepUp: (action) =>
    api.post("/admin/payouts/step-up", { action }).then((r) => r.data),
  overview: () => api.get("/admin/payouts/overview").then((r) => r.data),
  queue: (state) =>
    api.get("/admin/payouts/queue", { params: state ? { state } : {} }).then((r) => r.data),
  exceptions: () => api.get("/admin/payouts/exceptions").then((r) => r.data),
  detail: (ref) => api.get(`/admin/payouts/${ref}`).then((r) => r.data),
  requestInformation: (ref, body) =>
    api.post(`/admin/payouts/${ref}/request-information`, body).then((r) => r.data),
  hold: (ref, body) => api.post(`/admin/payouts/${ref}/hold`, body).then((r) => r.data),
  approve: (ref, body) => api.post(`/admin/payouts/${ref}/approve`, body || {}).then((r) => r.data),
  reject: (ref, body) => api.post(`/admin/payouts/${ref}/reject`, body || {}).then((r) => r.data),
  // release + batch actions mint a fresh server-verified step-up proof and pass it in the
  // X-Step-Up header. The proof is single-use and bound to this admin + action.
  release: (ref, body) =>
    withStepUp("release", (tok) =>
      api.post(`/admin/payouts/${ref}/release`, body || {},
        { headers: { "X-Step-Up": tok } }).then((r) => r.data)),
  funding: (key) => api.get(`/admin/payouts/funding/${key}`).then((r) => r.data),
  listBatches: () => api.get("/admin/payouts/batches").then((r) => r.data),
  batchDetail: (batchId) => api.get(`/admin/payouts/batches/${batchId}`).then((r) => r.data),
  createBatch: (body) =>
    withStepUp("create_batch", (tok) =>
      api.post("/admin/payouts/batches", body,
        { headers: { "X-Step-Up": tok } }).then((r) => r.data)),
  releaseBatch: (batchId) =>
    withStepUp("release_batch", (tok) =>
      api.post(`/admin/payouts/batches/${batchId}/release`, {},
        { headers: { "X-Step-Up": tok } }).then((r) => r.data)),
  reconcileBatch: (batchId, outcomes) =>
    api.post(`/admin/payouts/batches/${batchId}/reconcile`, { outcomes }).then((r) => r.data),
  resolveException: (instructionKey) =>
    api.post(`/admin/payouts/exceptions/${encodeURIComponent(instructionKey)}/resolve`, {})
      .then((r) => r.data),
  // internal super-admin ON/OFF control plane (DB override; override-first, env fallback)
  getSwitches: () => api.get("/admin/payouts/settings/switches").then((r) => r.data),
  setSwitches: (changes) =>
    withStepUp("configure", (tok) =>
      api.post("/admin/payouts/settings/switches", { changes },
        { headers: { "X-Step-Up": tok } }).then((r) => r.data)),
  getPolicy: () => api.get("/admin/payouts/settings/policy").then((r) => r.data),
  setPolicy: (version, limits) =>
    withStepUp("configure", (tok) =>
      api.post("/admin/payouts/settings/policy", { version, limits },
        { headers: { "X-Step-Up": tok } }).then((r) => r.data)),
  // Manual P2P rail: confirm a member's handle (ownership review) and settle an individual
  // manual payout after paying it by hand (an external reference is required to mark paid).
  manualConfirmDestination: (destId, note) =>
    api.post(`/admin/payouts/destinations/${destId}/manual-confirm`, { note: note || "" })
      .then((r) => r.data),
  markPaid: (ref, externalReference) =>
    api.post(`/admin/payouts/${ref}/mark-paid`, { external_reference: externalReference })
      .then((r) => r.data),
  markFailed: (ref, reason) =>
    api.post(`/admin/payouts/${ref}/mark-failed`, { reason: reason || "" }).then((r) => r.data),
};

// ---- public status labels (single source, plan §12) -----------------------
export const REQUEST_STATE_LABELS = {
  under_review: "Under review",
  awaiting_information: "Needs information",
  on_hold: "On hold",
  approved: "Approved",
  queued: "Preparing payment",
  processing: "Bank processing",
  paid: "Paid",
  rejected: "Rejected",
  cancelled: "Cancelled",
  failed: "Not completed",
  returned: "Returned",
};

export const REQUEST_STATE_TONE = {
  paid: "ok",
  approved: "ok",
  processing: "info",
  queued: "info",
  under_review: "neutral",
  awaiting_information: "warn",
  on_hold: "warn",
  rejected: "bad",
  failed: "bad",
  cancelled: "neutral",
  returned: "warn",
};

export function stateLabel(state) {
  return REQUEST_STATE_LABELS[state] || state;
}
